function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useField } from 'formik';
import PropTypes from 'prop-types';
import { useCombobox, useMultipleSelection } from 'downshift';
import styleNames from '@pareto-engineering/bem/exports';
import { Button } from "../../../../../b";
import { Popover } from "../../../../../a";
import { FormDescription, FormLabel } from "../../../..";
import { Menu } from "../Menu";

// Local Definitions

const baseClassName = styleNames.base;
const componentClassName = 'multiple';

/**
 *  @param {Array[Object]} first - first array to check if it has an item not in the second array.
 *  @param {Array[Object]} second - second array to check against the first array.
 *
 * @returns {Boolean} - true if the first array has an item not in the second array.
 */
const testIfArraysAreUnique = (first, second) => first.filter(objInFirstArray => !second.some(objInSecondArray => objInFirstArray.value === objInSecondArray.value)).length > 0;

/**
 * This is the component description.
 */
const Multiple = ({
  id,
  className: userClassName,
  style,
  name,
  label,
  labelColor,
  color,
  options,
  validate,
  optional,
  description,
  disabled,
  autoComplete,
  transformSearch,
  getTagColor,
  placeholder
  // ...otherProps
}) => {
  const [, meta, helpers] = useField({
    name,
    validate
  });
  const {
    setValue
  } = helpers;
  const {
    value
  } = meta;
  const [searchInputValue, setSearchInputValue] = useState('');
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
    selectedItems
  } = useMultipleSelection({
    initialSelectedItems: value || []
  });

  /**
   * @returns {Boolean} - Unique items from the options array so that the combobox
   * shows only the options that are not yet selected.
  */

  const items = useMemo(() => {
    const lowerCasedInputValue = searchInputValue.toLowerCase();
    return options.filter(option => {
      const lowerCasedOption = option.label.toLowerCase();
      return lowerCasedOption.includes(lowerCasedInputValue) && !selectedItems.includes(option);
    });
  }, [searchInputValue, selectedItems]);
  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps
  } = useCombobox({
    inputValue: searchInputValue,
    defaultHighlightedIndex: 0,
    // after selection, highlight the first item.
    selectedItem: null,
    items,
    circularNavigation: true,
    stateReducer: (state, actionAndChanges) => {
      const {
        changes,
        type
      } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true // keep the menu open after selection.
          };
        default:
          break;
      }
      return changes;
    },
    onStateChange: ({
      inputValue: newSearchInputValue,
      type,
      selectedItem
    }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          {
            const transformedInput = transformSearch(newSearchInputValue);
            setSearchInputValue(transformedInput);
            break;
          }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem && !selectedItem?.disabled) {
            setSearchInputValue('');
            addSelectedItem(selectedItem);
          }
          break;
        default:
          break;
      }
    }
  });
  useEffect(() => {
    setValue(selectedItems);
  }, [selectedItems]);
  useEffect(() => {
    if (value?.length > 0 && (testIfArraysAreUnique(value, selectedItems) || testIfArraysAreUnique(selectedItems, value))) {
      setSelectedItems(value);
    }
  }, [value]);
  const parentRef = useRef(null);
  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: [baseClassName, componentClassName, userClassName].filter(e => e).join(' '),
    style: style,
    ref: parentRef
  }, getComboboxProps()), label && /*#__PURE__*/React.createElement(FormLabel, _extends({
    className: [baseClassName, componentClassName].filter(e => e).join(' ')
  }, getLabelProps(), {
    name: name,
    optional: optional,
    color: labelColor
  }), label), selectedItems?.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "selected-items"
  }, selectedItems.map((selectedItem, index) => /*#__PURE__*/React.createElement("div", _extends({
    key: selectedItem.label
  }, getSelectedItemProps({
    selectedItem,
    index
  }), {
    className: "item"
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: e => {
      e.stopPropagation();
      removeSelectedItem(selectedItem);
    },
    isCompact: true,
    color: getTagColor ? getTagColor(selectedItem) : color
  }, /*#__PURE__*/React.createElement("span", null, selectedItem.label), /*#__PURE__*/React.createElement("span", {
    className: "icon close"
  }, "Y"))))), /*#__PURE__*/React.createElement("div", {
    className: "input-container"
  }, /*#__PURE__*/React.createElement("input", _extends({}, getInputProps(getDropdownProps({
    preventKeyAction: isOpen
  })), {
    className: "input",
    disabled: disabled,
    placeholder: placeholder,
    autoComplete: autoComplete
  }))), /*#__PURE__*/React.createElement(FormDescription, {
    className: "s-1",
    description: description,
    name: name
  }), /*#__PURE__*/React.createElement(Popover, {
    isOpen: isOpen,
    parentRef: parentRef
  }, /*#__PURE__*/React.createElement(Menu, _extends({
    className: `y-${color}`,
    isOpen: isOpen,
    getItemProps: getItemProps,
    highlightedIndex: highlightedIndex,
    items: items
  }, getMenuProps()))));
};
Multiple.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * A function that transforms the search input value
   */
  transformSearch: PropTypes.func,
  /**
   * The minimum length of the search input value
   */
  minLength: PropTypes.number,
  /**
   * The base color of the component
   */
  color: PropTypes.string
};
Multiple.defaultProps = {
  transformSearch: e => e,
  minLength: 2,
  color: 'interactive'
};
export default Multiple;